/**
 * Brazilian vehicle plate validation + masking.
 *
 * Two formats coexist:
 *   - Old (pré-2018):  AAA-9999       (3 letras + 4 dígitos)        ex: ABC-1234
 *   - Mercosul:        AAA-9A99       (3 letras + 1 dígito + 1 letra + 2 dígitos)
 *                                      ex: ABC-1D23
 *
 * Canonical regex covers both: third-from-end character is digit OR letter,
 * other positions fixed. Plates are normalized to uppercase, no separators,
 * for DB storage and equality checks.
 */

const PLATE_REGEX = /^[A-Z]{3}\d[A-Z\d]\d{2}$/;

/**
 * Canonical form: uppercase letters/digits only, no separators.
 *
 * normalizePlate("abc-1234")   → "ABC1234"
 * normalizePlate("abc 1d23")   → "ABC1D23"
 * normalizePlate("ABC1234")    → "ABC1234"
 * normalizePlate("")           → ""
 */
export function normalizePlate(plate: string): string {
  return plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/**
 * Returns true if the (possibly unformatted) string parses to a syntactically
 * valid Brazilian plate. Does NOT consult any external registry — purely
 * format validation.
 *
 * isValidPlate("ABC-1234")  → true   (old)
 * isValidPlate("ABC1D23")   → true   (Mercosul)
 * isValidPlate("AB-1234")   → false  (2 letras + 4 dígitos)
 * isValidPlate("1234ABC")   → false  (letras no fim)
 * isValidPlate("")          → false
 */
export function isValidPlate(plate: string): boolean {
  return PLATE_REGEX.test(normalizePlate(plate));
}

/**
 * Display mask, format-aware:
 *   - Old format: "ABC-1234" (hyphen after the 3rd letter — matches physical
 *     plate, which has the dash in the embossed lettering).
 *   - Mercosul: "ABC1D23" (no hyphen — the new plates ship without a
 *     separator, so we match the physical appearance instead of stamping
 *     a synthetic dash on them).
 *
 * Detection rule: once the 5th character has been typed, if it's a letter
 * we're in Mercosul territory and skip the hyphen. Until then we render
 * the old-format dash because most input flows pre-mid-2018 still see
 * old plates.
 *
 * maskPlate("abc1234")    → "ABC-1234"   (old)
 * maskPlate("abc1d23")    → "ABC1D23"    (Mercosul — no hyphen)
 * maskPlate("abc")        → "ABC"
 * maskPlate("abc1")       → "ABC-1"      (pre-Mercosul disambiguation)
 * maskPlate("")           → ""
 */
export function maskPlate(value: string): string {
  const clean = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
  if (clean.length <= 3) return clean;
  // Mercosul = 5th char is a letter. Until the 5th char appears, fall back
  // to old-format hyphen because partial Mercosul input is also a valid
  // partial old-format input.
  if (clean.length >= 5 && /^[A-Z]{3}\d[A-Z]/.test(clean)) {
    return clean;
  }
  return `${clean.slice(0, 3)}-${clean.slice(3)}`;
}

/**
 * Detects if a (valid) plate uses the new Mercosul format. Useful for UI
 * indicators or analytics. Pre-2018 plates have a digit in the 5th position;
 * Mercosul has a letter.
 *
 * isMercosulPlate("ABC1D23")  → true
 * isMercosulPlate("ABC1234")  → false
 * isMercosulPlate("ABC-1D23") → true (normalizes first)
 */
export function isMercosulPlate(plate: string): boolean {
  const norm = normalizePlate(plate);
  if (!PLATE_REGEX.test(norm)) return false;
  return /[A-Z]/.test(norm[4]!);
}
